import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  TextField,
  Button,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert,
  CircularProgress,
  useTheme,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

const DEMO_BUSINESS_NAME = 'Check-Now Ltd (Demo)';

interface Business {
  business_id: string;
  business_name: string;
}

interface TemplateOption {
  template_id: string;
  template_name: string;
  business_id?: string;
}

interface AssetTypeOption {
  asset_type_id: string;
  type_name: string;
  business_id?: string;
  business_name?: string;
}

const AddBusiness: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Form state
  const [businessName, setBusinessName] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');
  const [subscriptionTier, setSubscriptionTier] = useState('standard');
  const [accountEmails, setAccountEmails] = useState('');
  const [overviewEmails, setOverviewEmails] = useState('');
  const [overviewFreq, setOverviewFreq] = useState<'off' | 'daily' | 'weekly'>('daily');
  const [overviewTime, setOverviewTime] = useState('08:00');
  const [overviewDow, setOverviewDow] = useState<number>(1);
  
  // Copy-from source (e.g. Check-Now Ltd (Demo))
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [sourceBusinessId, setSourceBusinessId] = useState<string>('');
  const [sourceTemplates, setSourceTemplates] = useState<TemplateOption[]>([]);
  const [sourceAssetTypes, setSourceAssetTypes] = useState<AssetTypeOption[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [selectedAssetTypeIds, setSelectedAssetTypeIds] = useState<string[]>([]);
  const [loadingSourceData, setLoadingSourceData] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('Business created successfully! Redirecting to dashboard...');

  // Dropdown options
  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'inactive', label: 'Inactive' },
  ];

  const tierOptions = [
    { value: 'basic', label: 'Basic' },
    { value: 'standard', label: 'Standard' },
    { value: 'premium', label: 'Premium' },
    { value: 'enterprise', label: 'Enterprise' },
  ];

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios
      .get<Business[]>(`${API_BASE_URL}/businesses`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setBusinesses(list);
        const demo = list.find((b) => b.business_name === DEMO_BUSINESS_NAME);
        if (demo) setSourceBusinessId(demo.business_id);
      })
      .catch(() => setBusinesses([]));
  }, []);

  useEffect(() => {
    if (!sourceBusinessId) {
      setSourceTemplates([]);
      setSourceAssetTypes([]);
      setSelectedTemplateIds([]);
      setSelectedAssetTypeIds([]);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoadingSourceData(true);
    Promise.all([
      axios.get<TemplateOption[]>(`${API_BASE_URL}/check-templates/all-businesses`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get<{ data: AssetTypeOption[] }>(`${API_BASE_URL}/asset-types/all-businesses`, { headers: { Authorization: `Bearer ${token}` } }),
    ])
      .then(([templatesRes, assetTypesRes]) => {
        const allTemplates = Array.isArray(templatesRes.data) ? templatesRes.data : [];
        const rawAssetTypes = assetTypesRes.data?.data ?? assetTypesRes.data;
        const allAssetTypes = Array.isArray(rawAssetTypes) ? rawAssetTypes : [];
        const templates = allTemplates.filter((t) => t.business_id === sourceBusinessId);
        const assetTypes = allAssetTypes.filter((a) => a.business_id === sourceBusinessId);
        setSourceTemplates(templates);
        setSourceAssetTypes(assetTypes);
        setSelectedTemplateIds([]);
        setSelectedAssetTypeIds([]);
      })
      .catch(() => {
        setSourceTemplates([]);
        setSourceAssetTypes([]);
      })
      .finally(() => setLoadingSourceData(false));
  }, [sourceBusinessId, businesses]);

  const handleStatusChange = (event: SelectChangeEvent) => {
    setSubscriptionStatus(event.target.value);
  };

  const handleTierChange = (event: SelectChangeEvent) => {
    setSubscriptionTier(event.target.value);
  };

  const validateForm = () => {
    if (!businessName.trim()) return 'Business name is required';
    if (!businessEmail.trim()) return 'Business email is required';
    if (!validateEmail(businessEmail)) return 'Please enter a valid email address';
    if (!phoneNumber.trim()) return 'Phone number is required';
    if (!address.trim()) return 'Address is required';
    // Optional: validate comma-separated emails if provided
    const validateList = (value: string) =>
      value
        .split(',')
        .map((e) => e.trim())
        .filter((e) => e.length > 0)
        .every((e) => validateEmail(e));
    if (accountEmails.trim().length > 0 && !validateList(accountEmails)) return 'Account emails contain an invalid email';
    if (overviewEmails.trim().length > 0 && !validateList(overviewEmails)) return 'Overview emails contain an invalid email';
    return null;
  };

  const validateEmail = (email: string) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      const response = await axios.post<{ business_id: string }>(
        `${API_BASE_URL}/businesses`,
        {
          business_name: businessName,
          business_email: businessEmail,
          phone_number: phoneNumber,
          address: address,
          subscription_status: subscriptionStatus,
          subscription_tier: subscriptionTier,
          account_emails: accountEmails.trim() || null,
          overview_emails: overviewEmails.trim() || null,
          overview_email_frequency: overviewFreq,
          overview_email_time: overviewTime,
          overview_email_day_of_week: overviewFreq === 'weekly' ? overviewDow : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newBusinessId = response.data?.business_id;
      if (!newBusinessId) {
        setError('Business was created but no business_id was returned.');
        setLoading(false);
        return;
      }

      const shouldCopy = sourceBusinessId && (selectedTemplateIds.length > 0 || selectedAssetTypeIds.length > 0);
      if (shouldCopy) {
        try {
          const setupRes = await axios.post<{ success: boolean; copied_asset_types?: number; copied_templates?: number }>(
            `${API_BASE_URL}/businesses/${newBusinessId}/setup-from-source`,
            {
              source_business_id: sourceBusinessId,
              template_ids: selectedTemplateIds,
              asset_type_ids: selectedAssetTypeIds,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const at = setupRes.data?.copied_asset_types ?? 0;
          const tmpl = setupRes.data?.copied_templates ?? 0;
          setSuccessMessage(
            `Business created successfully. Copied ${at} asset type(s) and ${tmpl} template(s) from the source business. Redirecting to dashboard...`
          );
        } catch (setupErr: any) {
          const msg = setupErr.response?.data?.message || setupErr.message || 'Setup from source failed.';
          setError(`Business was created (ID: ${newBusinessId}) but copying setup failed: ${msg} You can retry setup from business settings.`);
          setLoading(false);
          return;
        }
      } else {
        setSuccessMessage('Business created successfully! Redirecting to dashboard...');
      }

      setSuccess(true);
      setBusinessName('');
      setBusinessEmail('');
      setPhoneNumber('');
      setAddress('');
      setSubscriptionStatus('active');
      setSubscriptionTier('standard');
      setAccountEmails('');
      setOverviewEmails('');
      setOverviewFreq('daily');
      setOverviewTime('08:00');
      setOverviewDow(1);
      setSourceBusinessId('');
      setSelectedTemplateIds([]);
      setSelectedAssetTypeIds([]);

      setTimeout(() => navigate('/owner/dashboard'), 2000);
    } catch (err: any) {
      console.error('Error creating business:', err);
      setError(err.response?.data?.message || 'Failed to create business. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
          Add New Business
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Business Name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                required
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business Email"
                value={businessEmail}
                onChange={(e) => setBusinessEmail(e.target.value)}
                required
                variant="outlined"
                type="email"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account Emails (comma-separated)"
                value={accountEmails}
                onChange={(e) => setAccountEmails(e.target.value)}
                variant="outlined"
                placeholder="accounts@acme.com, billing@acme.com"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Overview Emails (comma-separated)"
                value={overviewEmails}
                onChange={(e) => setOverviewEmails(e.target.value)}
                variant="outlined"
                placeholder="overview@acme.com, ops@acme.com"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="overview-freq-label">Overview Frequency</InputLabel>
                <Select
                  labelId="overview-freq-label"
                  value={overviewFreq}
                  label="Overview Frequency"
                  onChange={(e) => setOverviewFreq(e.target.value as any)}
                >
                  <MenuItem value="off">Off</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Send Time (HH:MM)"
                value={overviewTime}
                onChange={(e) => setOverviewTime(e.target.value)}
                variant="outlined"
                placeholder="08:00"
                inputProps={{ maxLength: 5 }}
              />
            </Grid>
            {overviewFreq === 'weekly' && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="overview-dow-label">Day of Week</InputLabel>
                  <Select
                    labelId="overview-dow-label"
                    value={overviewDow}
                    label="Day of Week"
                    onChange={(e) => setOverviewDow(Number(e.target.value))}
                  >
                    <MenuItem value={0}>Sunday</MenuItem>
                    <MenuItem value={1}>Monday</MenuItem>
                    <MenuItem value={2}>Tuesday</MenuItem>
                    <MenuItem value={3}>Wednesday</MenuItem>
                    <MenuItem value={4}>Thursday</MenuItem>
                    <MenuItem value={5}>Friday</MenuItem>
                    <MenuItem value={6}>Saturday</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
                variant="outlined"
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Copy setup from existing business
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel id="source-business-label">Source business</InputLabel>
                <Select
                  labelId="source-business-label"
                  value={sourceBusinessId}
                  label="Source business"
                  onChange={(e) => setSourceBusinessId(e.target.value)}
                >
                  <MenuItem value="">Don&apos;t copy</MenuItem>
                  {businesses.map((b) => (
                    <MenuItem key={b.business_id} value={b.business_id}>
                      {b.business_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {sourceBusinessId && (
                <Box sx={{ mt: 2 }}>
                  {loadingSourceData ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">Loading templates and asset types...</Typography>
                    </Box>
                  ) : (
                    <>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Select templates to copy</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', maxHeight: 160, gap: 0, mb: 2 }}>
                        {sourceTemplates.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">No templates in this business.</Typography>
                        ) : (
                          sourceTemplates.map((t) => (
                            <FormControlLabel
                              key={t.template_id}
                              control={
                                <Checkbox
                                  checked={selectedTemplateIds.includes(t.template_id)}
                                  onChange={(e) => {
                                    if (e.target.checked) setSelectedTemplateIds((prev) => [...prev, t.template_id]);
                                    else setSelectedTemplateIds((prev) => prev.filter((id) => id !== t.template_id));
                                  }}
                                />
                              }
                              label={t.template_name}
                            />
                          ))
                        )}
                      </Box>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Select asset types to copy</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', maxHeight: 160, gap: 0 }}>
                        {sourceAssetTypes.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">No asset types in this business.</Typography>
                        ) : (
                          sourceAssetTypes.map((a) => (
                            <FormControlLabel
                              key={a.asset_type_id}
                              control={
                                <Checkbox
                                  checked={selectedAssetTypeIds.includes(a.asset_type_id)}
                                  onChange={(e) => {
                                    if (e.target.checked) setSelectedAssetTypeIds((prev) => [...prev, a.asset_type_id]);
                                    else setSelectedAssetTypeIds((prev) => prev.filter((id) => id !== a.asset_type_id));
                                  }}
                                />
                              }
                              label={a.type_name}
                            />
                          ))
                        )}
                      </Box>
                      {(selectedTemplateIds.length > 0 || selectedAssetTypeIds.length > 0) && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          {selectedTemplateIds.length} template(s) and {selectedAssetTypeIds.length} asset type(s) selected.
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="subscription-status-label">Subscription Status</InputLabel>
                <Select
                  labelId="subscription-status-label"
                  value={subscriptionStatus}
                  label="Subscription Status"
                  onChange={handleStatusChange}
                  required
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="subscription-tier-label">Subscription Tier</InputLabel>
                <Select
                  labelId="subscription-tier-label"
                  value={subscriptionTier}
                  label="Subscription Tier"
                  onChange={handleTierChange}
                  required
                >
                  {tierOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/owner/dashboard')}
                disabled={loading}
              >
                Cancel
              </Button>
              
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {loading ? 'Creating...' : 'Create Business'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default AddBusiness; 